// Per-user "AI context" opt-out preferences for the scenaria (Scenar IA chat).
//
// Mirrors features/graph/util/slopifyContextPrefs.ts (the in-graph rail), but
// keyed by userId instead of contentId: a scenaria conversation has no single
// content, and the choice ("don't blend my org context into my scenarios") is
// naturally a per-user default that persists across conversations.
//
// The backend honours these same keys in
// api/gpt-conversation/utils/b4pt1ste_AI.js#fromUserFormResultsToPrompt.

const STORAGE_PREFIX = "scenaria-context-prefs:";

// The user's own message — always sent, shown locked in the UI for parity
// with the rail (it is never excludable).
export const SCENARIA_MANDATORY_LAYERS = ["humanPrompt"] as const;

export const SCENARIA_OPTIONAL_LAYERS = ["personalContext", "orgContext"] as const;

export type ScenariaOptionalLayer = typeof SCENARIA_OPTIONAL_LAYERS[number];

const keyFor = (userId: number | string): string => `${STORAGE_PREFIX}${userId}`;

export const getExcludedScenariaLayers = (userId: number | string): ScenariaOptionalLayer[] => {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is ScenariaOptionalLayer =>
      (SCENARIA_OPTIONAL_LAYERS as readonly string[]).includes(v),
    );
  } catch {
    return [];
  }
};

export const setExcludedScenariaLayers = (
  userId: number | string,
  excluded: ScenariaOptionalLayer[],
): void => {
  try {
    const unique = Array.from(new Set(excluded)).filter((v) =>
      (SCENARIA_OPTIONAL_LAYERS as readonly string[]).includes(v),
    );
    if (unique.length === 0) {
      localStorage.removeItem(keyFor(userId));
    } else {
      localStorage.setItem(keyFor(userId), JSON.stringify(unique));
    }
  } catch {
    /* ignore quota */
  }
};
